import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, getDoc, query, where } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { LocationProvider } from './LocationContext';
import SetupGuide from '../components/SetupGuide';
import './Admin.css';

const ROLES = [
  { key: 'admin', label: 'Admin' },
  { key: 'manager', label: 'Manager' },
  { key: 'cashier', label: 'Cashier' },
];

const hashPin = async (pin, salt) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(pin + salt);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

// Collapsible sidebar section (accordion). Persists per browser in
// localStorage and never hides the section containing the current page.
const NavSection = ({ title, paths, currentPath, children }) => {
  const storageKey = `navsec_${title}`;
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved === null ? true : saved === '1';
  });
  const containsActive = (paths || []).some((h) =>
    currentPath === h || currentPath.startsWith(h + '/') ||
    (h === '/admin/pos' && currentPath.startsWith('/admin/pos-sessions'))
  );
  // Auto-expand ONCE when the route moves into this section; after that the
  // user's manual collapse is always respected (no permanent override).
  useEffect(() => {
    if (containsActive && !open) {
      setOpen(true);
      localStorage.setItem(storageKey, '1');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);
  const effectiveOpen = open;
  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem(storageKey, next ? '1' : '0');
  };
  return (
    <>
      <li className="nav-header" onClick={toggle} style={{ cursor: 'pointer', userSelect: 'none' }} title="Click to collapse / expand">
        <i className={`fas fa-chevron-${effectiveOpen ? 'down' : 'right'}`} style={{ fontSize: '0.55rem', marginRight: 6 }}></i>
        {title}
      </li>
      {effectiveOpen && children}
    </>
  );
};

const AdminLayout = ({ children, title = 'Dashboard' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  // Kiosk session lock
  const [employees, setEmployees] = useState([]);
  const [lockVerified, setLockVerified] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockEmployeeId, setLockEmployeeId] = useState('');
  const [lockEmployeePin, setLockEmployeePin] = useState('');

  useEffect(() => {
    if (window.$ && window.adminlte) {
      window.$('body').Layout('fixLayoutHeight');
    }
  }, []);

  useEffect(() => {
    const auth = getAuth();
    
    const loadLocations = async (user) => {
      if (!user) return;

      try {
        const db = getFirestore();
        const snapshot = await getDocs(collection(db, 'users', user.uid, 'locations'));
        const locs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setLocations(locs);

        // Get selected location
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const selectedId = userDoc.data().selectedLocationId;
          const selected = locs.find((l) => l.id === selectedId);
          setSelectedLocation(selected || locs[0] || null);
        }
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };

    const checkKioskLock = async (user) => {
      if (!user) return;
      try {
        const lockRaw = localStorage.getItem('kioskSessionLock');
        if (!lockRaw) {
          setLockVerified(true);
          return;
        }
        const lockData = JSON.parse(lockRaw);
        // Only enforce if the lock belongs to the current user
        if (lockData.userId !== user.uid) {
          setLockVerified(true);
          return;
        }
        // Load employees with allowed roles
        const db = getFirestore();
        const empSnap = await getDocs(
          query(collection(db, 'users', user.uid, 'employees'), where('status', '==', 'active'))
        );
        const allowedRoles = ['admin', 'manager', 'cashier'];
        const emps = empSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((e) => allowedRoles.includes((e.role || '').toLowerCase()));
        setEmployees(emps);
        setShowLockModal(true);
        setLockEmployeeId('');
        setLockEmployeePin('');
      } catch (error) {
        console.error('Error checking kiosk lock:', error);
        setLockVerified(true);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      loadLocations(user);
      checkKioskLock(user);
    });

    return () => unsubscribe();
  }, []);

  // Sync sticky header with sidebar hover expand (AdminLTE sidebar-mini behavior)
  useEffect(() => {
    const sidebar = document.querySelector('.main-sidebar');
    if (!sidebar) return;

    const onEnter = () => {
      if (document.body.classList.contains('sidebar-collapse')) {
        document.body.classList.add('sidebar-hover-expand');
      }
    };
    const onLeave = () => {
      document.body.classList.remove('sidebar-hover-expand');
    };

    sidebar.addEventListener('mouseenter', onEnter);
    sidebar.addEventListener('mouseleave', onLeave);
    return () => {
      sidebar.removeEventListener('mouseenter', onEnter);
      sidebar.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  const toggleSidebar = (e) => {
    e.preventDefault();
    const isMobile = window.innerWidth < 992;
    if (isMobile) {
      document.body.classList.toggle('sidebar-open');
    } else {
      document.body.classList.toggle('sidebar-collapse');
    }
  };

  const closeSidebar = () => {
    document.body.classList.remove('sidebar-open');
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    const result = await Swal.fire({
      title: 'Logout?',
      text: 'Are you sure you want to logout?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, logout',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });
    
    if (result.isConfirmed) {
      try {
        const auth = getAuth();
        await signOut(auth);
        sessionStorage.clear();
        localStorage.clear();
        navigate('/admin/login');
      } catch (error) {
        console.error('Logout error:', error);
        navigate('/admin/login');
      }
    }
  };

  const isActive = (path) => {
    if (location.pathname === path || location.pathname.startsWith(path + '/')) return 'active';
    // Special case: pos-sessions should highlight Point of Sales
    if (path === '/admin/pos' && (location.pathname.startsWith('/admin/pos-sessions/') || location.pathname === '/admin/pos-sessions')) return 'active';
    return '';
  };

  const verifyLockEmployee = async () => {
    if (!lockEmployeeId) {
      Swal.fire('Error', 'Please select an employee', 'error');
      return;
    }
    const emp = employees.find((e) => e.id === lockEmployeeId);
    if (!emp) {
      Swal.fire('Error', 'Employee not found', 'error');
      return;
    }
    // Verify passkey if set
    if (emp.pinHash) {
      if (!lockEmployeePin || lockEmployeePin.length < 4) {
        Swal.fire('Error', 'Please enter the passkey (4-6 digits)', 'error');
        return;
      }
      const inputHash = await hashPin(lockEmployeePin, emp.pinSalt);
      if (inputHash !== emp.pinHash) {
        Swal.fire('Error', 'Incorrect passkey', 'error');
        return;
      }
    }
    // Verified — clear lock and allow access
    try {
      localStorage.removeItem('kioskSessionLock');
    } catch (e) {}
    setShowLockModal(false);
    setLockVerified(true);
  };

  const handleLocationSelect = async (loc) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    
    try {
      const db = getFirestore();
      await setDoc(doc(db, 'users', user.uid), { selectedLocationId: loc.id }, { merge: true });
      setSelectedLocation(loc);
      setShowLocationDropdown(false);
      // Reload page to refresh data with new location
      window.location.reload();
    } catch (error) {
      console.error('Error selecting location:', error);
    }
  };

  return (
    <div className="wrapper">
      {/* Navbar */}
      <nav className="main-header navbar navbar-expand navbar-white navbar-light">
        <ul className="navbar-nav">
          <li className="nav-item">
            <a className="nav-link" href="#" role="button" onClick={toggleSidebar}>
              <i className="fas fa-bars"></i>
            </a>
          </li>
          {location.pathname === '/admin/dashboard' && (
            <li className="nav-item d-none d-sm-inline-block">
              <a href="/admin/dashboard" className="nav-link">Home</a>
            </li>
          )}
        </ul>
        <ul className="navbar-nav ml-auto">
          {/* Location Selector Dropdown */}
          {locations.length > 0 && selectedLocation && (
            <li className="nav-item dropdown">
              <a 
                className="nav-link dropdown-toggle" 
                href="#" 
                role="button" 
                onClick={(e) => { e.preventDefault(); setShowLocationDropdown(!showLocationDropdown); }}
              >
                <i className="fas fa-store mr-1"></i>
                <span className="d-none d-md-inline">{selectedLocation.companyName || selectedLocation.locationName || 'Select Location'}</span>
              </a>
              {showLocationDropdown && (
                <div className="dropdown-menu dropdown-menu-right show" style={{ right: 0, left: 'auto' }}>
                  <span className="dropdown-header">Select Location</span>
                  <div className="dropdown-divider"></div>
                  {locations.map((loc) => (
                    <a 
                      key={loc.id}
                      href="#"
                      className={`dropdown-item ${selectedLocation?.id === loc.id ? 'active' : ''}`}
                      onClick={(e) => { e.preventDefault(); handleLocationSelect(loc); }}
                    >
                      <i className={`fas ${selectedLocation?.id === loc.id ? 'fa-check-circle text-success' : 'fa-circle text-muted'} mr-2`}></i>
                      {loc.companyName || loc.locationName}
                    </a>
                  ))}
                </div>
              )}
            </li>
          )}
          <li className="nav-item">
            <a className="nav-link" data-widget="fullscreen" href="#" role="button">
              <i className="fas fa-expand-arrows-alt"></i>
            </a>
          </li>
          <li className="nav-item">
            <a className="nav-link" href="/admin/settings" role="button">
              <i className="fas fa-cog"></i>
            </a>
          </li>
          <li className="nav-item">
            <a className="nav-link" href="/admin/logout" role="button" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt"></i>
            </a>
          </li>
        </ul>
      </nav>

      {/* Main Sidebar */}
      <aside className="main-sidebar sidebar-light-primary elevation-3">
        <a href="/admin/dashboard" className="brand-link">
          <img src="/fodyfee_logo.png" alt="FodyFee" className="brand-image elevation-1" style={{ opacity: 0.8 }} />
          <span className="brand-text font-weight-light">FodyFee</span>
        </a>
        <div className="sidebar">
          <nav className="mt-2">
            <ul className="nav nav-pills nav-sidebar flex-column" data-widget="treeview" role="menu" data-accordion="false">
              {/* Order mirrors the Setup Guide's logical progression */}
              <li className="nav-item">
                <a href="/admin/dashboard" className={`nav-link ${isActive('/admin/dashboard')}`}>
                  <i className="nav-icon fas fa-home"></i>
                  <p>Home</p>
                </a>
              </li>
              <li className="nav-item">
                 <a href="/admin/fody-flight" className={`nav-link ${isActive('/admin/fody-flight')}`}>
                   <img src="/fodyAI.png" alt="Fody" className="fody-shine" style={{ width: 20, height: 20, objectFit: 'contain', marginRight: 8, flexShrink: 0 }} />
                   <p style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', margin: 0, lineHeight: 1 }}>
                     Fody Flight
                     <span style={{ fontSize: '0.55rem', fontWeight: 700, color: '#FF5722', background: 'rgba(255,87,34,0.15)', padding: '1px 4px', borderRadius: 4, border: '1px solid rgba(255,87,34,0.25)', textTransform: 'uppercase', flexShrink: 0 }}>BETA</span>
                   </p>
                   <span className="right badge badge-warning" style={{ fontSize: '0.6rem', background: '#FF5722', color: 'white', flexShrink: 0 }}>AI</span>
                 </a>
              </li>

              <NavSection title="GETTING STARTED" paths={['/admin/account', '/admin/locations']} currentPath={location.pathname}>
              <li className="nav-item">
                <a href="/admin/account" className={`nav-link ${isActive('/admin/account')}`}>
                  <i className="nav-icon fas fa-user"></i>
                  <p>Account</p>
                </a>
              </li>
              <li className="nav-item">
                <a href="/admin/locations" className={`nav-link ${isActive('/admin/locations')}`}>
                  <i className="nav-icon fas fa-map-marker-alt"></i>
                  <p>Locations</p>
                </a>
              </li>

              </NavSection>

              <NavSection title="MENU" paths={['/admin/categories', '/admin/products', '/admin/product-options']} currentPath={location.pathname}>
              <li className="nav-item">
                <a href="/admin/categories" className={`nav-link ${isActive('/admin/categories')}`}>
                  <i className="nav-icon fas fa-tags"></i>
                  <p>Categories</p>
                </a>
              </li>
              <li className="nav-item">
                <a href="/admin/products" className={`nav-link ${isActive('/admin/products')}`}>
                  <i className="nav-icon fas fa-box"></i>
                  <p>Products</p>
                </a>
              </li>
              <li className="nav-item">
                <a href="/admin/product-options" className={`nav-link ${isActive('/admin/product-options')}`}>
                  <i className="nav-icon fas fa-sliders-h"></i>
                  <p>Products Options</p>
                </a>
              </li>

              </NavSection>

              <NavSection title="INVENTORY" paths={['/admin/ingredients', '/admin/suppliers', '/admin/inventory-movements', '/admin/expenses']} currentPath={location.pathname}>
              <li className="nav-item">
                <a href="/admin/ingredients" className={`nav-link ${isActive('/admin/ingredients')}`}>
                  <i className="nav-icon fas fa-carrot"></i>
                  <p>Ingredients</p>
                </a>
              </li>
              <li className="nav-item">
                <a href="/admin/suppliers" className={`nav-link ${isActive('/admin/suppliers')}`}>
                  <i className="nav-icon fas fa-truck"></i>
                  <p>Suppliers</p>
                </a>
              </li>
              <li className="nav-item">
                <a href="/admin/inventory-movements" className={`nav-link ${isActive('/admin/inventory-movements')}`}>
                  <i className="nav-icon fas fa-arrow-right-arrow-left"></i>
                  <p>Movements Log</p>
                </a>
              </li>
              <li className="nav-item">
                <a href="/admin/expenses" className={`nav-link ${isActive('/admin/expenses')}`}>
                  <i className="nav-icon fas fa-money-bill-wave"></i>
                  <p>Expenses &amp; Losses</p>
                </a>
              </li>

              </NavSection>

              <NavSection title="OPERATIONS" paths={['/admin/floor-plan', '/admin/workzones', '/admin/reservations', '/admin/team']} currentPath={location.pathname}>
              <li className="nav-item">
                <a href="/admin/floor-plan" className={`nav-link ${isActive('/admin/floor-plan')}`}>
                  <i className="nav-icon fas fa-drafting-compass"></i>
                  <p>Floor Plan</p>
                </a>
              </li>
              <li className="nav-item">
                <a href="/admin/workzones" className={`nav-link ${isActive('/admin/workzones')}`}>
                  <i className="nav-icon fas fa-th-large"></i>
                  <p>Workzones</p>
                </a>
              </li>
              <li className="nav-item">
                <a href="/admin/reservations" className={`nav-link ${isActive('/admin/reservations')}`}>
                  <i className="nav-icon fas fa-calendar-check"></i>
                  <p>Reservations</p>
                </a>
              </li>

              <li className="nav-item">
                <a href="/admin/team" className={`nav-link ${isActive('/admin/team')}`}>
                  <i className="nav-icon fas fa-users-cog"></i>
                  <p>Team Management</p>
                </a>
              </li>

              </NavSection>

              <NavSection title="SALES" paths={['/admin/sales', '/admin/sales-reports', '/admin/customers']} currentPath={location.pathname}>
              <li className="nav-item">
                <a href="/admin/sales" className={`nav-link ${isActive('/admin/sales')}`}>
                  <i className="nav-icon fas fa-chart-line"></i>
                  <p>Sales</p>
                </a>
              </li>
              <li className="nav-item">
                <a href="/admin/sales-reports" className={`nav-link ${isActive('/admin/sales-reports')}`}>
                  <i className="nav-icon fas fa-chart-pie"></i>
                  <p>Sales Reports</p>
                </a>
              </li>
              <li className="nav-item">
                <a href="/admin/customers" className={`nav-link ${isActive('/admin/customers')}`}>
                  <i className="nav-icon fas fa-users"></i>
                  <p>Customers</p>
                </a>
              </li>

              </NavSection>

              <NavSection title="DEVICE MANAGEMENT" paths={['/admin/pos', '/admin/kds-stations', '/admin/vkiosks', '/admin/kiosk']} currentPath={location.pathname}>
              <li className="nav-item">
                <a href="/admin/pos" className={`nav-link ${isActive('/admin/pos')}`}>
                  <i className="nav-icon fas fa-cash-register"></i>
                  <p>Point of Sales</p>
                </a>
              </li>
              <li className="nav-item">
                <a href="/admin/kds-stations" className={`nav-link ${isActive('/admin/kds-stations')}`}>
                  <i className="nav-icon fas fa-tv"></i>
                  <p>Kitchen Displays</p>
                </a>
              </li>
              <li className="nav-item">
                <a href="/admin/vkiosks" className={`nav-link ${isActive('/admin/vkiosks') || isActive('/admin/kiosk')}`}>
                  <i className="nav-icon fas fa-desktop"></i>
                  <p>Self Service Kiosks</p>
                </a>
              </li>

              </NavSection>

              <NavSection title="GROW" paths={['/admin/online-ordering']} currentPath={location.pathname}>
              <li className="nav-item">
                <a href="/admin/online-ordering" className={`nav-link ${isActive('/admin/online-ordering')}`}>
                  <i className="nav-icon fas fa-globe"></i>
                  <p>Online Ordering</p>
                </a>
              </li>



              </NavSection>

              <li className="nav-item mt-3">
                <a href="/admin/logout" className="nav-link" onClick={handleLogout}>
                  <i className="nav-icon fas fa-sign-out-alt"></i>
                  <p>Logout</p>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </aside>

      {/* Content Wrapper */}
      <div className="content-wrapper" style={{ minHeight: '100vh' }}>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">{title}</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <LocationProvider value={{ selectedLocation, locations }}>
              {showLockModal && !lockVerified ? null : children}
            </LocationProvider>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div className="sidebar-overlay" onClick={closeSidebar}></div>

      {/* Footer */}
      <footer className="main-footer">
        <div className="float-right d-none d-sm-inline">
          Made with <span>❤️</span> in Gilroy, California.
          <b>Version</b> 1.0
        </div>
        <strong>Copyright &copy; {new Date().getFullYear()} FodyFee</strong>
      </footer>

      {/* Kiosk Session Lock Modal */}
      {showLockModal && !lockVerified && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 24,
              width: '100%',
              maxWidth: 480,
              padding: '32px 28px',
              boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ fontSize: 24, fontWeight: 800, color: '#1a1a1a', marginBottom: 8, textAlign: 'center' }}>
              <i className="fas fa-user-shield" style={{ color: '#ff6b35', marginRight: 10 }}></i>
              Staff Verification Required
            </div>
            <div style={{ fontSize: 16, color: '#888', marginBottom: 24, textAlign: 'center' }}>
              A kiosk session is active. Please verify your identity to access the admin panel.
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 14, fontWeight: 700, color: '#555', marginBottom: 6, display: 'block' }}>Employee</label>
              <select
                value={lockEmployeeId}
                onChange={(e) => { setLockEmployeeId(e.target.value); setLockEmployeePin(''); }}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: 12,
                  border: '1px solid #e0e0e0',
                  fontSize: 16,
                  background: '#fff',
                }}
              >
                <option value="">Select employee...</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name} ({ROLES.find((r) => r.key === emp.role)?.label || emp.role})
                  </option>
                ))}
              </select>
            </div>

            {lockEmployeeId && employees.find((e) => e.id === lockEmployeeId)?.pinHash && (
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 14, fontWeight: 700, color: '#555', marginBottom: 6, display: 'block' }}>Passkey</label>
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={lockEmployeePin}
                  onChange={(e) => setLockEmployeePin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 4-6 digit passkey"
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: 12,
                    border: '1px solid #e0e0e0',
                    fontSize: 18,
                    fontWeight: 700,
                    letterSpacing: 4,
                    textAlign: 'center',
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => navigate('/admin/vkiosks')}
                style={{
                  flex: 1,
                  padding: '14px 0',
                  borderRadius: 12,
                  border: '1px solid #e0e0e0',
                  background: '#fff',
                  color: '#555',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={verifyLockEmployee}
                style={{
                  flex: 1,
                  padding: '14px 0',
                  borderRadius: 12,
                  border: 'none',
                  background: '#ff6b35',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Verify & Continue
              </button>
            </div>
          </div>
        </div>
      )}
      <SetupGuide />
    </div>
  );
};

export default AdminLayout;